import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Icon } from '../../../layout/Icon.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import { useAdjustStock } from '../hooks/useAdjust.js';

const TYPES = [
  { key: 'adjustment', label: 'تسوية يدوية' },
  { key: 'damage', label: 'تالف' }
];

export function AdjustModal({ open, product, onClose }) {
  const [type, setType] = useState('adjustment');
  const [delta, setDelta] = useState(0);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const adjust = useAdjustStock();

  useEffect(() => {
    if (open) {
      setType('adjustment');
      setDelta(0);
      setNotes('');
      setError('');
    }
  }, [open, product]);

  if (!open || !product) return null;

  const current = Number(product.currentStock || 0);
  const next = current + Number(delta || 0);
  const invalid = !Number(delta) || next < 0;

  async function submit() {
    setError('');
    if (!Number(delta)) { setError('الكمية لا يمكن أن تكون صفراً'); return; }
    if (next < 0) { setError('لا يمكن أن يصبح المخزون سالباً'); return; }
    try {
      await adjust.mutateAsync({
        productId: product._id || product.id,
        warehouseId: product.warehouseId?._id || product.warehouseId,
        qty: Math.round(Number(delta)),
        type,
        notes: notes || ''
      });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error?.message || err.message);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: 'rgba(0,0,0,.45)' }}
    >
      <div className="card w-[460px] max-w-full" onClick={(e) => e.stopPropagation()}>
        <div className="card-h">
          <h3 className="text-[15px] font-bold flex-1">تسوية مخزون</h3>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="card-b space-y-3">
          <div className="bg-paper-2 rounded-[10px] p-3 flex items-center gap-2">
            <span className="text-[24px]">{product.emoji || '📦'}</span>
            <div className="flex-1 min-w-0">
              <div className="font-bold">{product.name}</div>
              <div className="text-[11.5px] text-ink-3" style={{ fontFamily: 'monospace' }}>{product.sku}</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] text-ink-3">المخزون الحالي</div>
              <div className="font-bold text-[16px]">{current}</div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">نوع الحركة</label>
            <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px] w-full">
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setType(t.key)}
                  className={clsx(
                    'flex-1 px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold transition-colors',
                    type === t.key ? 'bg-orange text-white shadow-orange-glow' : 'text-ink-2 hover:bg-white'
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">
              الكمية (موجبة للزيادة، سالبة للنقصان)
            </label>
            <input
              className="input"
              type="number"
              value={delta}
              onChange={(e) => setDelta(e.target.value)}
              placeholder="مثلاً: -3 أو 5"
            />
          </div>

          <div className="flex items-center justify-between bg-paper-2 rounded-[10px] px-3 py-2">
            <span className="text-[12px] text-ink-2">المخزون بعد التسوية</span>
            <Pill variant={next < 0 ? 'danger' : next === 0 ? 'warning' : 'success'}>
              {next}
            </Pill>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">ملاحظات</label>
            <textarea
              className="input"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="سبب التسوية..."
            />
          </div>

          {error ? <div className="text-red text-[12px] font-semibold">{error}</div> : null}
        </div>
        <div className="card-h border-b-0 border-t flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose} disabled={adjust.isPending}>
            إلغاء
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={invalid || adjust.isPending}
          >
            {adjust.isPending ? 'جاري الحفظ...' : 'تسجيل التسوية'}
          </button>
        </div>
      </div>
    </div>
  );
}
